import "./styles.less";

import React, { useCallback, useEffect, useRef, useState } from "react";

import { CheckOutlined } from "@ant-design/icons";
import { ImagenOptimizada } from "../../Components/Image/web";
import { LocationTag } from "../../Components/Property/LocationTag/web";
import { PageLayout } from "../../../src/Components/Layout/web";
import { PriceTag } from "../../Components/Property/PriceTag/web";
import { PropComponentMode } from "../../Components/Property/PropertyInterfaces";
import { PropertyMap } from "../../Components/Property/PropertyMap/web";
import { Typography } from "antd";
import { TypologyTag } from "../../Components/Property/TypologyTag/web";
import debounce from "lodash.debounce";
import { usePropertyDetailsTwo } from "../PropertyDetails/PropertyDetailsTwo.hook";
import { LocationOutlinedIcon } from "shared-components/Components/CustomIcons/LocationOutlineIcon/web";

interface PropertyDetailsPrintProps {
	id: string;
	mode?: PropComponentMode;
}

export function PropertyDetailsPrint({ id, mode = "auto" }: PropertyDetailsPrintProps) {
	const { listing } = usePropertyDetailsTwo(id, mode);
	const CLASS_NAME = "print-prop";
	const [isPrint, setIsPrint] = useState(false);
	const contentRef = useRef(null);
	const regex = /(<([^>]+)>)/gi;
	const description = listing.description.replace(regex, "");

	const debouncedContent = useCallback(
		debounce(value => {
			setIsPrint(value);
		}, 1500),
		[]
	);

	useEffect(() => {
		if (isPrint) window.print();

		contentRef.current.classList.add(CLASS_NAME);
		const existContent = document.getElementsByClassName(CLASS_NAME).length != 0;
		if (existContent) debouncedContent(true);

		return () => {
			contentRef.current.classList.remove(CLASS_NAME);
		};
	}, [isPrint]);

	return (
		<div ref={contentRef}>
			<PageLayout classContainer="property-details-print" withFooter={false}>
				<div className="container">
					{/* Title - Contact */}
					{isPrint && (
						<div className="print-block">
							<div className="container-title-contact">
								<Typography.Title className="print-block" level={4}>
									{listing?.title}
								</Typography.Title>
								<Typography.Title
									className="small"
									level={
										4
									}>{`Contactar a ${listing?.owner?.name}`}</Typography.Title>
							</div>
						</div>
					)}

					{/* Technical Sheet */}
					{listing?.technicalSheet.length != 0 && (
						<div className="section">
							<div className="container-title-price">
								<Typography.Title level={4}>Detalles de la Propiedad </Typography.Title>
								<Typography.Title level={3}>
									<span className="typeOperation">
										{listing?.operation_type?.name}
									</span>
									<PriceTag
										price={listing?.price}
										temporalCurrency={listing?.temporal_currency}
									/>
								</Typography.Title>
							</div>
							<div className="d-flex content">
								<div className="container-technicalSheet">
									{listing?.technicalSheet.map(item => (
										<div className="item-technicalSheet" key={item.field}>
											<CheckOutlined />
											<Typography.Text>
												{`${item.text}: `}
												<span className="res">
													{item.value !== null
														? item.value
														: "¡Preguntale!"}
												</span>
											</Typography.Text>
										</div>
									))}
								</div>
								<div className="container-image-features">
									<ImagenOptimizada src={listing?.img} alt={listing?.title} />
									<TypologyTag
										propertyType={listing?.property_type?.id}
										operationType={listing?.operation_type?.id}
										bedrooms={listing?.bedrooms}
										rooms={listing?.rooms}
										bathrooms={listing?.bathrooms}
										m2={listing?.minM2 > 0 ? listing?.minM2 : listing?.m2}
										guests={listing?.guests}
										loading={false}
										className="property-typology-tag-desktop features"
										showIcons
									/>
								</div>
							</div>
						</div>
					)}

					{/* Facilities */}
					{listing?.facilities.length != 0 && (
						<div className="section">
							<Typography.Title className="title" level={4}>
								Comodidades de la propiedad
							</Typography.Title>
							<div className="content container-facilities">
								{listing?.facilities.map(facilitie => (
									<div className="item-facilitie" key={facilitie.id}>
										<CheckOutlined />
										<Typography.Text>{facilitie.name}</Typography.Text>
									</div>
								))}
							</div>
						</div>
					)}

					{/* Description */}
					{description && (
						<div className="section">
							<Typography.Title className="title" level={4}>
								Descripción
							</Typography.Title>
							<div className="content container-description">
								<Typography.Paragraph className="property-description">
									{description}
								</Typography.Paragraph>
							</div>
						</div>
					)}

					{/* Map */}
					{listing?.latitude && listing?.longitude && (
						<div className="section">
							<div className="container-title-map">
								<Typography.Title level={4}>Ubicación</Typography.Title>
								<div className="address">
									<LocationOutlinedIcon />
									<LocationTag id={listing?.id} mode={mode} />
								</div>
							</div>
							<div className="content container-map">
								<div className="map">
									<PropertyMap
										latitude={listing?.latitude}
										longitude={listing?.longitude}
										isProject={listing?.isProject}
										id={listing?.id || id}
										width={"100%"}
										height={"100%"}
										pointType={listing?.pointType}
										mode={mode}
										options={{
											scrollWheelZoom: false,
											dragging: false,
											zoomControl: false,
										}}
									/>
								</div>
							</div>
						</div>
					)}
				</div>
			</PageLayout>
		</div>
	);
}
